import type {
  Invite,
  InviteAcceptResult,
  InviteCreated,
  ManagedStaffCreated,
  ManagedStaffUpdated,
  InvitePreview,
  AssignableMember,
  HandoverResult,
  Member,
  MemberPermissionOverrides,
  MemberChannelAccess,
  Approval,
  SeatUsage,
  Role,
  RolesResponse,
} from "../types";
import type { AgentPresenceRow, Team, RoutingRuleRow, TeamDetail } from "../routing-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";

export type TeamApiMethods = Pick<
  ApiClient,
  | "listRoles"
  | "ensureStaffPresets"
  | "listMemberChannelAccess"
  | "createRole"
  | "updateRole"
  | "deleteRole"
  | "listRoutingRules"
  | "createRoutingRule"
  | "updateRoutingRule"
  | "deleteRoutingRule"
  | "reorderRoutingRules"
  | "listTeams"
  | "getTeam"
  | "createTeam"
  | "updateTeam"
  | "setTeamBotContinuation"
  | "createMeetLink"
  | "deleteTeam"
  | "setTeamMembers"
  | "setTeamHours"
  | "listAgentPresence"
  | "agentHeartbeat"
  | "setAgentAvailability"
  | "handoverShift"
  | "listAssignableMembers"
  | "listMembers"
  | "changeMemberRole"
  | "getMemberPermissionOverrides"
  | "getMemberChannelAccess"
  | "setMemberChannelAccess"
  | "setMemberPermissionOverrides"
  | "transferOwnership"
  | "getSeatUsage"
  | "requestSeats"
  | "listApprovals"
  | "decideApproval"
  | "removeMember"
  | "listInvites"
  | "createInvite"
  | "resendInvite"
  | "createManagedStaff"
  | "updateManagedStaff"
  | "revokeInvite"
  | "previewInvite"
  | "acceptInvite"
>;

export function createTeamApi({ request, ws }: HttpTransport): TeamApiMethods {
  return {
    listRoles: (id) => request<RolesResponse>("GET", `${ws(id)}/roles`),
    ensureStaffPresets: async (id) =>
      (await request<{ roles: Role[] }>("POST", `${ws(id)}/roles/staff-presets`)).roles,
    listMemberChannelAccess: async (id) =>
      (await request<{ members: Record<string, string[]> }>("GET", `${ws(id)}/team/channel-access`))
        .members,
    createRole: (id, input) => request<Role>("POST", `${ws(id)}/roles`, input),
    updateRole: (id, roleId, input) =>
      request<Role>("PATCH", `${ws(id)}/roles/${encodeURIComponent(roleId)}`, input),
    async deleteRole(id, roleId) {
      await request("DELETE", `${ws(id)}/roles/${encodeURIComponent(roleId)}`);
    },
    async listRoutingRules(id) {
      return (await request<{ rules: RoutingRuleRow[] }>("GET", `${ws(id)}/routing-rules`)).rules;
    },
    createRoutingRule: (id, input) =>
      request<RoutingRuleRow>("POST", `${ws(id)}/routing-rules`, input),
    updateRoutingRule: (id, ruleId, patch) =>
      request<RoutingRuleRow>(
        "PATCH",
        `${ws(id)}/routing-rules/${encodeURIComponent(ruleId)}`,
        patch,
      ),
    async deleteRoutingRule(id, ruleId) {
      await request<void>("DELETE", `${ws(id)}/routing-rules/${encodeURIComponent(ruleId)}`);
    },
    async reorderRoutingRules(id, orderedIds) {
      return (
        await request<{ rules: RoutingRuleRow[] }>("PUT", `${ws(id)}/routing-rules/order`, {
          orderedIds,
        })
      ).rules;
    },
    async listTeams(id) {
      return (await request<{ teams: Team[] }>("GET", `${ws(id)}/teams`)).teams;
    },
    getTeam: (id, teamId) =>
      request<TeamDetail>("GET", `${ws(id)}/teams/${encodeURIComponent(teamId)}`),
    createTeam: (id, input) => request<Team>("POST", `${ws(id)}/teams`, input),
    updateTeam: (id, teamId, input) =>
      request<Team>("PATCH", `${ws(id)}/teams/${encodeURIComponent(teamId)}`, input),
    setTeamBotContinuation: (id, teamId, enabled) =>
      request<Team>("PATCH", `${ws(id)}/teams/${encodeURIComponent(teamId)}/bot-continuation`, {
        enabled,
      }),
    createMeetLink: (id, conversationId) =>
      request<{ meetUrl: string }>(
        "POST",
        `${ws(id)}/conversations/${encodeURIComponent(conversationId)}/meet-link`,
        {},
      ),
    async deleteTeam(id, teamId) {
      await request<void>("DELETE", `${ws(id)}/teams/${encodeURIComponent(teamId)}`);
    },
    setTeamMembers: (id, teamId, members) =>
      request<TeamDetail>("PUT", `${ws(id)}/teams/${encodeURIComponent(teamId)}/members`, {
        members,
      }),
    setTeamHours: (id, teamId, hours) =>
      request<TeamDetail>("PUT", `${ws(id)}/teams/${encodeURIComponent(teamId)}/business-hours`, {
        hours,
      }),
    async listAgentPresence(id) {
      return (await request<{ agents: AgentPresenceRow[] }>("GET", `${ws(id)}/presence`)).agents;
    },
    async agentHeartbeat(id) {
      await request<unknown>("POST", `${ws(id)}/presence/heartbeat`, {});
    },
    async setAgentAvailability(id, availability) {
      await request<unknown>("PUT", `${ws(id)}/presence/status`, { availability });
    },
    handoverShift: (id, input) => request<HandoverResult>("POST", `${ws(id)}/handover`, input),
    async listAssignableMembers(id) {
      return (await request<{ members: AssignableMember[] }>("GET", `${ws(id)}/team/assignable`))
        .members;
    },
    async listMembers(id) {
      return (await request<{ members: Member[] }>("GET", `${ws(id)}/members`)).members;
    },
    changeMemberRole: (id, membershipId, roleId) =>
      request<Member>("PATCH", `${ws(id)}/members/${encodeURIComponent(membershipId)}`, {
        roleId,
      }),
    getMemberChannelAccess: (id, userId) =>
      request<MemberChannelAccess>(
        "GET",
        `${ws(id)}/team/members/${encodeURIComponent(userId)}/channel-access`,
      ),
    setMemberChannelAccess: (id, userId, channelIds) =>
      request<MemberChannelAccess>(
        "PUT",
        `${ws(id)}/team/members/${encodeURIComponent(userId)}/channel-access`,
        { channelIds },
      ),
    getMemberPermissionOverrides: (id, userId) =>
      request<MemberPermissionOverrides>(
        "GET",
        `${ws(id)}/team/members/${encodeURIComponent(userId)}/permission-overrides`,
      ),
    setMemberPermissionOverrides: (id, userId, overrides) =>
      request<MemberPermissionOverrides>(
        "PUT",
        `${ws(id)}/team/members/${encodeURIComponent(userId)}/permission-overrides`,
        { overrides },
      ),
    transferOwnership: (id, membershipId, password) =>
      request<Member>(
        "POST",
        `${ws(id)}/members/${encodeURIComponent(membershipId)}/transfer-ownership`,
        { password },
      ),
    getSeatUsage: (id) => request<SeatUsage>("GET", `${ws(id)}/seats`),
    requestSeats: (id, input) =>
      request<{ ticketId: string }>("POST", `${ws(id)}/team/seat-request`, input),
    async listApprovals(id, status) {
      const query = status ? `?status=${status}` : "";
      return (await request<{ approvals: Approval[] }>("GET", `${ws(id)}/approvals${query}`))
        .approvals;
    },
    decideApproval: (id, approvalId, decision, note) =>
      request<Approval>(
        "POST",
        `${ws(id)}/approvals/${encodeURIComponent(approvalId)}/${decision}`,
        { note },
      ),
    async removeMember(id, membershipId) {
      await request("DELETE", `${ws(id)}/members/${encodeURIComponent(membershipId)}`);
    },
    async listInvites(id) {
      return (await request<{ invites: Invite[] }>("GET", `${ws(id)}/invites`)).invites;
    },
    createInvite: (id, input) => request<InviteCreated>("POST", `${ws(id)}/invites`, input),
    createManagedStaff: (id, input) =>
      request<ManagedStaffCreated>("POST", `${ws(id)}/team/staff`, input),
    updateManagedStaff: (id, membershipId, input) =>
      request<ManagedStaffUpdated>(
        "PATCH",
        `${ws(id)}/team/staff/${encodeURIComponent(membershipId)}`,
        input,
      ),
    resendInvite: (id, inviteId) =>
      request<InviteCreated>("POST", `${ws(id)}/invites/${encodeURIComponent(inviteId)}/resend`),
    async revokeInvite(id, inviteId) {
      await request("DELETE", `${ws(id)}/invites/${encodeURIComponent(inviteId)}`);
    },
    previewInvite: (token) =>
      request<InvitePreview>("GET", `/v1/team/invites/${encodeURIComponent(token)}`),
    acceptInvite: (token, input) =>
      request<InviteAcceptResult>(
        "POST",
        `/v1/team/invites/${encodeURIComponent(token)}/accept`,
        input,
      ),
  };
}
